import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import type { PersistedDraft } from '../../application/models';
import type { DraftStore } from '../../application/ports';
import type { TestamentId as TestamentIdValue } from '../../domain';
import { revivePersistedDraft } from './persisted-draft-revival';

export const DRAFT_DATABASE_VERSION = 2;
export const DRAFT_STORE_NAME = 'drafts';
const UPDATED_AT_INDEX = 'by-updated-at';

interface DraftDatabase extends DBSchema {
  readonly drafts: {
    readonly key: string;
    readonly value: PersistedDraft;
    readonly indexes: { readonly 'by-updated-at': string };
  };
}

export class IndexedDbDraftStore implements DraftStore {
  private readonly database: Promise<IDBPDatabase<DraftDatabase>>;

  constructor(databaseName = 'mon-testament') {
    this.database = openDB<DraftDatabase>(databaseName, DRAFT_DATABASE_VERSION, {
      upgrade(database, oldVersion, _newVersion, transaction) {
        const store =
          oldVersion < 1
            ? database.createObjectStore(DRAFT_STORE_NAME)
            : transaction.objectStore(DRAFT_STORE_NAME);
        store.createIndex(UPDATED_AT_INDEX, 'updatedAt.value');
      },
    });
  }

  async load(id: TestamentIdValue): Promise<PersistedDraft | null> {
    const draft = await (await this.database).get(DRAFT_STORE_NAME, id.value);
    return draft === undefined ? null : revivePersistedDraft(draft);
  }

  async save(draft: PersistedDraft): Promise<void> {
    await (await this.database).put(DRAFT_STORE_NAME, draft, draft.testament.id.value);
  }

  async delete(id: TestamentIdValue): Promise<void> {
    await (await this.database).delete(DRAFT_STORE_NAME, id.value);
  }

  async close(): Promise<void> {
    (await this.database).close();
  }
}
